/**
 * /app/{slug}/people/org-chart — reporting-hierarchy chart.
 *
 * Two modes (docs/50 Phase A):
 *  - Pro (tenant has the "org_chart_pro" feature): an interactive React Flow
 *    builder — drag/auto-layout, shapes/colours, color-by + legend, filters,
 *    search/spotlight, persisted overrides (OrgChartPro → OrgChartBuilder).
 *  - Free (no feature) OR ?view=simple: the original read-only CSS flex-tree
 *    (OrgChartSimple), which stays the print/no-JS fallback.
 *
 * RBAC: HR-admin only (company_admin / hr_manager via isHRAdmin). Non-HR roles
 * see a denial card. Topology source: GET /api/v1/directory (active employees;
 * manager_id tree) for BOTH modes. The Pro builder also reads/writes the
 * authored chart via /api/v1/org-charts.
 */

import { lazy, Suspense } from "react";
import { z } from "zod";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  LayoutDashboard,
  Network,
  ShieldX,
  Sparkles,
  Users,
} from "lucide-react";
import { useDirectory } from "@/hooks/use-directory";
import { useAuth, useFeatures } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { toApiError } from "@/lib/api";
import { FeatureOrgChartPro } from "@/types/org-chart";
import { PageHeader } from "@/components/shared/page-header";
import { ModuleNav } from "@/components/shared/module-nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { OrgChartSimple } from "@/components/org-chart/org-chart-simple";
import { useBelowSm } from "@/lib/use-below-sm";

// The Pro builder pulls in React Flow + elkjs — lazy-load it so Free tenants
// (and the simple-view path) never download the heavy canvas bundle.
const OrgChartPro = lazy(() =>
  import("@/components/org-chart/org-chart-pro").then((m) => ({
    default: m.OrgChartPro,
  })),
);

const orgChartSearchSchema = z.object({
  // ?view=simple forces the read-only flex-tree even on a Pro tenant.
  view: z.enum(["simple", "builder"]).optional().catch(undefined),
});

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/people/org-chart",
)({
  validateSearch: orgChartSearchSchema,
  component: OrgChartRoute,
});

function OrgChartRoute() {
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { has } = useFeatures();
  const allowed = isHRAdmin(user?.role);
  const hasPro = has(FeatureOrgChartPro);
  // Builder mode when the feature is on AND not forced to simple. Phones
  // default to the simple tree — the canvas builder needs a larger screen —
  // but can still open the builder on purpose (?view=builder).
  const phone = useBelowSm();
  const builderMode =
    hasPro &&
    (search.view === "builder" || (search.view !== "simple" && !phone));

  // The directory is the topology source for BOTH modes. The Pro builder also
  // loads it internally; here we use it for the role-only (Free) path + the
  // shared loading/empty/error shells.
  const dir = useDirectory({ includeInactive: false, enabled: allowed });

  // ── access denied ────────────────────────────────────────────────────
  if (!allowed) {
    return (
      <>
        <PageHeader
          title="Org Chart"
          description="Visual reporting hierarchy for the company."
        />
        <ModuleNav />
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <ShieldX className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-medium text-gray-900">
                You don&apos;t have access
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                The org chart is visible to HR managers and company admins
                only. Ask your HR team if you need to see the reporting
                structure.
              </p>
            </div>
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
                <ArrowLeft className="h-4 w-4" />
                Back to People
              </Link>
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  // ── shared header actions: back + view toggle (Pro tenants only) ───────
  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      {hasPro && (
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() =>
            void navigate({
              to: "/app/$tenantSlug/people/org-chart",
              params: { tenantSlug },
              search: builderMode ? { view: "simple" } : { view: "builder" },
            })
          }
        >
          {builderMode ? (
            <>
              <LayoutDashboard className="h-4 w-4" />
              Simple View
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Builder View
            </>
          )}
        </Button>
      )}
      <Button asChild variant="outline" size="sm" className="gap-1.5">
        <Link to="/app/$tenantSlug/people" params={{ tenantSlug }}>
          <ArrowLeft className="h-4 w-4" />
          Back to People
        </Link>
      </Button>
    </div>
  );

  // ── Pro builder ────────────────────────────────────────────────────────
  if (builderMode) {
    return (
      <>
        <PageHeader
          title="Org Chart"
          description="Drag, style, and lay out your reporting structure. Changes save automatically."
          actions={headerActions}
        />
        <ModuleNav />
        <div className="mb-3 flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
          {phone ? (
            <span>
              The builder works best on a larger screen. Use Simple View to
              read the chart on a phone.
            </span>
          ) : (
            <span>
              Pro builder — drag people to reposition, click a node to style it,
              colour-by department/level, and search to spotlight someone. The
              topology stays in sync with your directory.
            </span>
          )}
        </div>
        <Suspense
          fallback={
            <div className="space-y-3">
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-[70vh] w-full rounded-xl" />
            </div>
          }
        >
          <OrgChartPro />
        </Suspense>
      </>
    );
  }

  // ── Free / simple flex-tree (shares the directory load) ────────────────
  if (dir.isLoading) {
    return (
      <>
        <PageHeader
          title="Org Chart"
          description="Visual reporting hierarchy for the company."
          actions={headerActions}
        />
        <ModuleNav />
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders are positional
            <Skeleton key={i} className="h-28 w-44 rounded-xl" />
          ))}
        </div>
      </>
    );
  }

  if (dir.isError) {
    return (
      <>
        <PageHeader
          title="Org Chart"
          description="Visual reporting hierarchy for the company."
          actions={headerActions}
        />
        <ModuleNav />
        <Alert variant="destructive">
          <AlertTitle>Could not load the directory</AlertTitle>
          <AlertDescription>{toApiError(dir.error).message}</AlertDescription>
        </Alert>
      </>
    );
  }

  const activeCount = dir.entries.filter((e) => e.is_active).length;

  if (activeCount === 0) {
    return (
      <>
        <PageHeader
          title="Org Chart"
          description="Visual reporting hierarchy for the company."
          actions={headerActions}
        />
        <ModuleNav />
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground">
              <Users className="h-6 w-6" />
            </span>
            <p className="max-w-sm text-sm text-muted-foreground">
              No active employees yet. Add your team first, then come back here
              to see the org chart.
            </p>
            <Button asChild className="gap-1.5">
              <Link to="/app/$tenantSlug/people/new" params={{ tenantSlug }}>
                <Users className="h-4 w-4" />
                Add Employees
              </Link>
            </Button>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Org Chart"
        description={`Reporting hierarchy · ${activeCount} active employee${activeCount !== 1 ? "s" : ""}`}
        actions={headerActions}
      />
      <ModuleNav />
      <div className="mb-4 flex items-center gap-2 rounded-lg border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        <Network className="h-3.5 w-3.5 shrink-0" />
        <span>
          Nodes deeper than 3 levels are collapsed — click the{" "}
          <strong>+N more</strong> button to expand a branch. Only active
          employees are shown.
        </span>
      </div>
      <OrgChartSimple entries={dir.entries} myId={user?.user_id} />
    </>
  );
}
